import React, { useState } from 'react';

import ChangePlanButton from 'components/payment/ChangePlanButton.jsx';
import UpdatePaymentMethod from 'components/payment/UpdatePaymentMethod.jsx';
import UpgradeToAnnualModal from 'components/payment/UpgradeToAnnualModal.jsx';

// One customer's one subscription. A customer can hold several at once (a website plan and
// a Dielines API plan), so MySubscription renders one of these per subscription and each
// carries its own plan name, price, card and actions. Everything it needs arrives on the
// subscription prop -- it does no fetching of its own.
const SubscriptionCard = ({ subscription, link_to_manage, onChanged }) => {

  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);

  if (subscription.is_paypal) {
    return (
      <>
        <p> {t("sign_up.subscription_managed_on_paypal")} </p> <br />
        <p> {t("sign_up.cancel_on_paypal")} </p>
      </>
    )
  }

  if (!subscription.is_stripe) {
    return null;
  }

  // The Dielines API plans carry their interval inside a longer type (monthly_dielines_30),
  // so match on the prefix. An exact match against 'monthly' left them rendering a bare
  // "USD 59.90" with no period next to it.
  const subscriptionType = subscription.subscription_type || '';
  let per = '';
  if (subscriptionType.startsWith('annually')) {
    per = t('sign_up.per_year');
  } else if (subscriptionType.startsWith('monthly')) {
    per = t('sign_up.per_month');
  }

  return (
    <>
      {subscription.past_due && (
        <div className="alert alert-warning subscription-past-due-alert">
          <span dangerouslySetInnerHTML={{__html: t("sign_up.subscription_past_due")}} ></span>
        </div>
      )}

      <div className="subscription-card">
        <div className="subscription-card-icon" aria-hidden="true">
          <svg viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg" fill="none">
            <rect x="10" y="26" width="50" height="36" rx="5" fill="#ffffff" />
            <rect x="10" y="32" width="50" height="5" fill="rgba(0,0,0,0.18)" />
            <rect x="16" y="44" width="10" height="7" rx="1.5" fill="#eb5424" opacity="0.7" />
            <circle cx="32" cy="55" r="1.6" fill="#eb5424" opacity="0.55" />
            <circle cx="37" cy="55" r="1.6" fill="#eb5424" opacity="0.55" />
            <circle cx="42" cy="55" r="1.6" fill="#eb5424" opacity="0.55" />
            <circle cx="47" cy="55" r="1.6" fill="#eb5424" opacity="0.55" />
            <circle cx="60" cy="22" r="14" fill="#ffffff" />
            <path d="M 60 14 A 8 8 0 1 1 52 22" fill="none" stroke="#eb5424" strokeWidth="3.5" strokeLinecap="round" />
            <polygon points="52,12 47,21 57,21" fill="#eb5424" />
          </svg>
        </div>

        <div className="subscription-card-body">
          <div className="subscription-card-header">
            <div className="subscription-card-title">
              <span className="subscription-card-label">{t('nav.my_subscription')}</span>
              {subscription.description && (
                <h4 className="subscription-card-plan">{subscription.description}</h4>
              )}
            </div>
            {subscription.next_payment_amount && (
              <div className="subscription-card-price">
                <span className="subscription-card-amount">{subscription.next_payment_amount}</span>
                {per && <span className="subscription-card-period"> / {per}</span>}
              </div>
            )}
          </div>

          <div className="subscription-card-meta">
            {!subscription.will_renew && (
              <span className="subscription-card-meta-item subscription-card-meta-warning">
                <i className="fa fa-exclamation-circle" aria-hidden="true"></i>
                {t('nav.subscription_will_not_be_renewed')}
              </span>
            )}
            {!subscription.past_due && subscription.will_renew && subscription.next_payment_date && (
              <span className="subscription-card-meta-item">
                <i className="fa fa-calendar-o" aria-hidden="true"></i>
                <strong>{t('sign_up.next_payment_date')}</strong> {subscription.next_payment_date}
              </span>
            )}
            {subscription.payment_method_name && (
              <span className="subscription-card-meta-item">
                <i className="fa fa-credit-card" aria-hidden="true"></i>
                {subscription.payment_method_name}
              </span>
            )}
            {subscription.can_upgrade_to_annual && subscription.annual_price_saving_formatted && (
              <a
                className="subscription-card-upgrade-link"
                href="#"
                onClick={(event) => { event.preventDefault(); setUpgradeModalOpen(true); }}
              >
                {t('sign_up.change_to_annual_cta')}
                <span className="subscription-card-upgrade-link-saving">
                  <i className="fa fa-tag" aria-hidden="true"></i>
                  <span dangerouslySetInnerHTML={{__html: t("sign_up.save_per_year").replace('%{saving}', subscription.annual_price_saving_formatted)}} ></span>
                </span>
              </a>
            )}
          </div>

          {subscription.stripe_payment_method_id && (
            <div className="subscription-card-actions">
              {/* Names the subscription being re-carded. Without it the server falls back to
                  the customer's first active subscription, which for anyone holding two is
                  not necessarily the one whose button they pressed. */}
              <UpdatePaymentMethod stripeId={subscription.stripe_id} />
              {/* stripeId names the subscription being switched. Without it the server falls
                  back to the customer's oldest subscription, which for anyone holding more
                  than one is not necessarily the card they clicked. */}
              <ChangePlanButton
                options={subscription.tier_change_options}
                currentPlanDescription={subscription.description}
                stripeId={subscription.stripe_id}
              />
              {link_to_manage && (
                <a className="btn subscription-card-action-secondary" href="/my_subscriptions">
                  {t('payment.manage_subscription')}
                </a>
              )}
            </div>
          )}
        </div>
      </div>

      {upgradeModalOpen && (
        <UpgradeToAnnualModal
          stripeId={subscription.stripe_id}
          onClose={() => setUpgradeModalOpen(false)}
          onSuccess={() => { setUpgradeModalOpen(false); onChanged(); }}
        />
      )}
    </>
  )
}

export default SubscriptionCard;
